import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ScheduleAssignmentWithDetails } from '../services/scheduleAssignmentService';

interface CalendarEventTooltipProps {
  event: {
    id: string;
    title: string;
    start: Date;
    end: Date;
    resource: ScheduleAssignmentWithDetails;
  };
  onDelete: (assignment: ScheduleAssignmentWithDetails) => void;
}

const CalendarEventTooltip: React.FC<CalendarEventTooltipProps> = ({ event, onDelete }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const eventRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const assignment = event.resource;

  // Calculate tooltip position using fixed positioning (portal)
  useEffect(() => {
    if (showTooltip && eventRef.current) {
      const eventRect = eventRef.current.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 280;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const padding = 10;

      let top: number;
      let left: number;

      // Try positioning below the event first
      if (eventRect.bottom + tooltipHeight + padding < viewportHeight) {
        top = eventRect.bottom + padding;
        left = eventRect.left + (eventRect.width / 2) - (tooltipWidth / 2);
      } else if (eventRect.top - tooltipHeight - padding > 0) {
        // Position above
        top = eventRect.top - tooltipHeight - padding;
        left = eventRect.left + (eventRect.width / 2) - (tooltipWidth / 2);
      } else {
        // Position to the right
        top = eventRect.top;
        left = eventRect.right + padding;
      }

      // Ensure tooltip stays within viewport bounds
      left = Math.max(padding, Math.min(left, viewportWidth - tooltipWidth - padding));
      top = Math.max(padding, Math.min(top, viewportHeight - tooltipHeight - padding));

      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 99999,
      });
    }
  }, [showTooltip]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 150);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete(assignment);
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const duration = Math.floor((event.end.getTime() - event.start.getTime()) / 1000);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'playing':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'completed':
        return 'bg-gray-100 text-gray-600 border-gray-300';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'skipped':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  // Tooltip content rendered via portal
  const tooltipContent = showTooltip ? createPortal(
    <div
      ref={tooltipRef}
      style={tooltipStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-[320px]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-900 text-sm truncate" title={assignment.template_name}>
              {assignment.template_name || 'Unknown Template'}
            </h4>
            <p className="text-xs text-gray-500 truncate" title={assignment.channel_name}>
              {assignment.channel_name}
            </p>
          </div>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium border flex-shrink-0 ${getStatusColor(
              assignment.status
            )}`}
          >
            {assignment.status}
          </span>
        </div>

        {/* Details */}
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <span className="text-gray-600">Start: </span>
              <span className="font-medium text-gray-900">{event.start.toLocaleString()}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div className="flex-1">
              <span className="text-gray-600">End: </span>
              <span className="font-medium text-gray-900">{event.end.toLocaleString()}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div className="flex-1">
              <span className="text-gray-600">Duration: </span>
              <span className="font-medium text-gray-900">{formatDuration(duration)}</span>
            </div>
          </div>

          {assignment.schedule_video_count && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <div className="flex-1">
                <span className="text-gray-600">Videos: </span>
                <span className="font-medium text-gray-900">{assignment.schedule_video_count}</span>
              </div>
            </div>
          )}

          {assignment.notes && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-gray-500 italic line-clamp-2" title={assignment.notes}>
                {assignment.notes}
              </p>
            </div>
          )}
        </div>

        {/* Delete button */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={handleDelete}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 rounded-lg transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Schedule
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div
      ref={eventRef}
      className="relative h-full w-full cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Event content */}
      <div className="px-1.5 py-0.5 h-full overflow-hidden">
        <span className="text-xs font-medium truncate block">{event.title}</span>
      </div>

      {/* Tooltip rendered via portal to body */}
      {tooltipContent}
    </div>
  );
};

export default CalendarEventTooltip;

